import React, { useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Papa from "papaparse";
import { db } from "../../utils/firebase";
import {
  doc,
  collection,
  setDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Menu from "../../components/features/Menu/Menu";

const DashboardImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [mappedColumns, setMappedColumns] = useState<Record<string, string>>(
    {}
  );
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [showMappingPopup, setShowMappingPopup] = useState(false);

  // Your expected columns
  const appColumns = [
    "Projet",
    "Client",
    "Client final",
    "Étape de facturation",
    "Montant",
    "Produit",
    "Début du projet",
    "Fin du projet",
    "Date de paiement",
  ];

  /** Handle file upload and parse CSV */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      Papa.parse(selectedFile, {
        header: true,
        complete: (results) => {
          if (!results.data || !results.data.length) {
            alert("Le fichier CSV est vide ou invalide.");
            return;
          }
          setParsedData(results.data as any[]);
          setCsvColumns(Object.keys(results.data[0]));
          setShowMappingPopup(true);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          alert("Error parsing CSV file.");
        },
      });
    }
  };

  /** Called when user selects a CSV column for each app column */
  const handleMappingChange = (appColumn: string, csvColumn: string) => {
    setMappedColumns((prev) => ({ ...prev, [appColumn]: csvColumn }));
  };

  /** Groups rows by client & writes them to Firestore */
  const confirmMapping = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        alert("Vous devez être connecté pour importer des données.");
        return;
      }

      // 1) Build a dictionary: clientName -> array of project rows
      const clientToProjects: Record<string, any[]> = {};

      for (const row of parsedData) {
        // Map CSV columns to your app field names
        const clientName = row[mappedColumns["Client"]] || "Client_SansNom";

        // Build the row data object, normalising amounts & dates
        const mappedRow = appColumns.reduce((acc, appColumn) => {
          const csvCol = mappedColumns[appColumn];
          let value = csvCol ? row[csvCol] : null;

          // ── ensure Montant is stored as a number ────────────────────────────────
          if (appColumn === "Montant" && value !== null) {
            value = Number(String(value).replace(/[^\d.-]/g, ""));
          }

          // ── convert date‑like strings to Firestore Timestamp ────────────────────
          if (
            ["Début du projet", "Fin du projet", "Date de paiement"].includes(
              appColumn
            ) &&
            value
          ) {
            const d = new Date(value);
            value = isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
          }

          acc[appColumn] = value;
          return acc;
        }, {} as Record<string, any>);

        // Derived fields to make front‑end aggregations fast
        if (mappedRow["Date de paiement"] instanceof Timestamp) {
          const jsDate = mappedRow["Date de paiement"].toDate();
          mappedRow.year = jsDate.getFullYear(); // e.g. 2024
          mappedRow.month = jsDate.getMonth() + 1; // 1 – 12
          // Identify project owner for scoped queries
          mappedRow.userId = user.uid;
        }

        // Group by client
        if (!clientToProjects[clientName]) {
          clientToProjects[clientName] = [];
        }
        clientToProjects[clientName].push(mappedRow);
      }

      // 2) Use batch writes for performance
      let batch = writeBatch(db);
      let batchCounter = 0;

      // /users/{uid}/clients/{clientId}/projects/{projectId}
      for (const [clientName, projects] of Object.entries(clientToProjects)) {
        // clientId is a sanitized string
        const clientId = clientName
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^\w_-]/g, "");

        // Create/update the client doc
        const clientRef = doc(db, "users", user.uid, "clients", clientId);
        batch.set(clientRef, {
          clientName,
          // Optionally add more summary fields here
        });
        batchCounter++;

        // For each project row, create a doc in subcollection "projects"
        for (const projectData of projects) {
          const projectRef = doc(collection(clientRef, "projects"));
          batch.set(projectRef, projectData);
          batchCounter++;

          // If we approach 500 writes, commit and start new batch
          if (batchCounter >= 500) {
            await batch.commit();
            batch = writeBatch(db);
            batchCounter = 0;
          }
        }
      }

      // Commit any leftover writes
      if (batchCounter > 0) {
        await batch.commit();
      }

      alert("Données importées avec succès !");
    } catch (error) {
      console.error("Error importing data:", error);
      alert(
        "Erreur lors de l’import. Consultez la console pour plus de détails."
      );
    } finally {
      setShowMappingPopup(false);
    }
  };

  return (
    <div className="flex h-screen w-screen">
      <Menu />
      <div className="min-h-screen w-full bg-gray-100 p-8 overflow-auto">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-semibold mb-6">Revenus & Dépenses</h1>
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-medium border-b pb-4 mb-6">
              Graphique des revenus
            </h2>
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
              <InformationCircleIcon className="h-10 w-10 mb-4 text-gray-400" />
              <p className="mb-2 font-medium">Aucune donnée pour l’instant</p>
              <p className="text-sm mb-6">
                Veuillez importer votre CSV pour générer votre dashboard
              </p>
              <button
                className="bg-gray-200 hover:bg-gray-300 transition duration-150 px-4 py-2 rounded-lg text-sm font-medium text-gray-700"
                onClick={() =>
                  document
                    .querySelector<HTMLInputElement>("input[type=file]")
                    ?.click()
                }
              >
                Importer CSV
              </button>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
      </div>

      {showMappingPopup && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Importer des projets</h3>
            <div className="space-y-4">
              {appColumns.map((appColumn) => (
                <div key={appColumn} className="flex justify-between">
                  <span>{appColumn}</span>
                  <select
                    className="border px-2 py-1 rounded"
                    onChange={(e) =>
                      handleMappingChange(appColumn, e.target.value)
                    }
                  >
                    <option value="">Sélectionnez la colonne</option>
                    {csvColumns.map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <button
              className="mt-6 bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
              onClick={confirmMapping}
            >
              Importer
            </button>
            <button
              className="mt-6 ml-4 text-gray-500"
              onClick={() => setShowMappingPopup(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardImport;
